/**
 * The single launch splash (see bootStore.ts). It stays mounted from the first React render until the first
 * screen underneath is fully painted, then plays the splash exit and unmounts. Never remounted, so the
 * splash timeline (started by the inline script in index.html) never restarts.
 */
import { useEffect, useState } from 'react';
import { SplashView } from '../SplashView';
import { markBootDone, markRevealing, sinceBoot, useBootState } from './bootStore';

/** iOS AppRootView.minimumSplashDuration. */
export const MIN_SPLASH_MS = 2400;
/** Never hold a ready screen hostage to a slow image. */
const DECODE_CAP_MS = 2500;
const EXIT_CAP_MS = 2200;
export const SCREEN_ROOT_ID = 'app-screen';

export function BootOverlay() {
  const { sessionReady, screenMounted, phase } = useBootState();
  const [exiting, setExiting] = useState(false);
  // Dev previews (/dev/*) never get the launch splash.
  const [isDevPath] = useState(() => window.location.pathname === '/dev' || window.location.pathname.startsWith('/dev/'));
  useEffect(() => { if (isDevPath) markBootDone(); }, [isDevPath]);

  useEffect(() => {
    if (isDevPath || !sessionReady || !screenMounted) return;
    let cancelled = false;
    void (async () => {
      await Promise.race([screenPainted(), wait(DECODE_CAP_MS)]);
      const remaining = MIN_SPLASH_MS - sinceBoot();
      if (remaining > 0) await wait(remaining);
      if (cancelled) return;
      markRevealing();
      setExiting(true);
    })();
    return () => { cancelled = true; };
  }, [isDevPath, sessionReady, screenMounted]);

  // Safety net: the screen is revealed even if the splash exit never reports back.
  useEffect(() => {
    if (!exiting) return;
    const id = window.setTimeout(markBootDone, EXIT_CAP_MS);
    return () => window.clearTimeout(id);
  }, [exiting]);

  if (phase === 'done' || isDevPath) return null;
  return (
    <div className="fixed inset-0 z-[1000]" aria-hidden={exiting || undefined}>
      <SplashView exiting={exiting} onExited={markBootDone} />
    </div>
  );
}

function wait(ms: number) {
  return new Promise<void>((resolve) => { window.setTimeout(resolve, ms); });
}

function nextFrame() {
  return new Promise<void>((resolve) => { requestAnimationFrame(() => { requestAnimationFrame(() => resolve()); }); });
}

/** Fonts loaded and every image of the first screen decoded (lazy chunks inside it get one extra frame). */
async function screenPainted() {
  await nextFrame();
  await document.fonts?.ready.catch(() => undefined);
  const root = document.getElementById(SCREEN_ROOT_ID);
  if (!root) return;
  const images = [...root.querySelectorAll('img')];
  await Promise.all(images.map((img) => (img.complete && img.naturalWidth > 0 ? Promise.resolve() : img.decode().catch(() => undefined))));
  await nextFrame();
}
